import React, { ReactNode } from 'react';

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'default' | 'primary' | 'secondary';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: ReactNode;
  className?: string;
  withDot?: boolean;
}

const Badge: React.FC<BadgeProps> = ({ 
  variant = 'default',
  size = 'md',
  children,
  className = '',
  withDot = false,
}) => {
  // Define variant specific classes with gradients and enhanced styling
  const variantClasses = {
    default: 'bg-gray-800/70 text-gray-300 border-gray-600/50',
    success: 'bg-gradient-to-r from-green-900/50 to-emerald-900/40 text-green-300 border-green-600/40',
    warning: 'bg-gradient-to-r from-amber-900/50 to-yellow-900/40 text-amber-300 border-yellow-600/40',
    danger: 'bg-gradient-to-r from-red-900/50 to-rose-900/40 text-red-300 border-red-600/40',
    info: 'bg-gradient-to-r from-blue-900/50 to-cyan-900/40 text-blue-300 border-blue-600/40',
    primary: 'bg-gradient-to-r from-indigo-900/50 to-violet-900/40 text-indigo-300 border-indigo-600/40',
    secondary: 'bg-gradient-to-r from-purple-900/50 to-fuchsia-900/40 text-purple-300 border-purple-600/40'
  };

  // Define size classes
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1 text-sm'
  };

  // Define dot colors
  const dotColors = {
    default: 'bg-gray-400',
    success: 'bg-green-400',
    warning: 'bg-amber-400',
    danger: 'bg-red-400',
    info: 'bg-blue-400',
    primary: 'bg-indigo-400',
    secondary: 'bg-purple-400'
  };

  return (
    <span 
      className={`
        inline-flex items-center justify-center rounded-full font-medium
        shadow-sm backdrop-blur-sm border
        transition-all duration-200 hover:shadow-md
      ${variantClasses[variant]}
        ${sizeClasses[size]}
      ${className}
      `}
    >
      {withDot && (
        <span 
          className={`inline-block h-2 w-2 rounded-full mr-1.5 ${dotColors[variant]} animate-pulse shadow-sm shadow-${variant}-500/40`}
        />
      )}
      {children}
    </span>
  );
};

export default Badge; 